// Category Tree editor controller.
//
// Node rendering (and expand/collapse) comes from Ui::TreeComponent, which uses
// native <details>/<summary> — no JS is needed to toggle a single node. This
// controller adds the editor-only behaviour on top: expand/collapse all, a text
// filter, and HTML5 drag-and-drop reordering that PATCHes reorderUrl.

import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

export default class extends Controller {
  static targets = [
    "dropZone",
    "children",
    "searchInput",
    "invalidMoveToast",
    "errorToast",
  ]
  static values = {
    reorderUrl: String,
  }

  connect() {
    // Match the previous editor: the tree starts fully expanded.
    this.allDetails().forEach((d) => (d.open = true))
  }

  // --- Expand / collapse all ---

  expandAll(event) {
    event.preventDefault()
    this.animateReflow(() => this.allDetails().forEach((d) => (d.open = true)))
  }

  collapseAll(event) {
    event.preventDefault()
    this.animateReflow(() => this.allDetails().forEach((d) => (d.open = false)))
  }

  // FLIP (First-Last-Invert-Play): glide nodes to their new position when a
  // bulk expand/collapse changes the layout. Restores the smooth motion the
  // pre-<details> editor had — the native single-node <details> toggle stays
  // instant, but expand/collapse-all shifts many rows at once and reads as a
  // jarring jump without this. Nodes that shift translate from their old spot;
  // nodes revealed by the expand fade + slide in. Uses the Web Animations API
  // with fill "none" so no transform residue is left on the element.
  animateReflow(mutate) {
    // Respect users who opt out of motion — apply the change with no animation.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      mutate()
      return
    }

    const DURATION = 200
    const EASING = "cubic-bezier(0.23, 1, 0.32, 1)"
    const nodes = () =>
      Array.from(this.element.querySelectorAll("[data-category-id]"))

    // FIRST: record the top of every currently-visible node.
    const first = new Map()
    nodes().forEach((node) => {
      if (node.offsetParent !== null) {
        first.set(node, node.getBoundingClientRect().top)
      }
    })

    // LAST: apply the open/close change; layout jumps to its final state.
    mutate()
    void this.element.offsetHeight // force reflow so measurements are fresh

    // INVERT + PLAY: glide shifted nodes from their old top, fade in new ones.
    nodes().forEach((node) => {
      if (node.offsetParent === null) return // still hidden — nothing to animate

      if (first.has(node)) {
        const delta = first.get(node) - node.getBoundingClientRect().top
        if (Math.abs(delta) > 0.5) {
          node.animate(
            [
              { transform: `translateY(${delta}px)` },
              { transform: "translateY(0)" },
            ],
            { duration: DURATION, easing: EASING },
          )
        }
      } else {
        node.animate(
          [
            { opacity: 0, transform: "translateY(-6px)" },
            { opacity: 1, transform: "translateY(0)" },
          ],
          { duration: DURATION, easing: EASING },
        )
      }
    })
  }

  // --- Search / filter ---

  filterTree() {
    const query = this.searchInputTarget.value.toLowerCase().trim()
    const nodes = this.element.querySelectorAll("[data-category-id]")

    if (!query) {
      nodes.forEach((node) => (node.style.display = ""))
      return
    }

    // Hide everything, then reveal matches and their ancestor chain.
    nodes.forEach((node) => (node.style.display = "none"))
    nodes.forEach((node) => {
      const nameEl = node.querySelector(".truncate")
      if (!nameEl) return
      if (!nameEl.textContent.toLowerCase().includes(query)) return

      let current = node
      while (current) {
        current.style.display = ""
        // Open the enclosing <details> so revealed descendants are visible.
        const parentDetails = current.parentElement?.closest("details")
        if (parentDetails) parentDetails.open = true
        current = current.parentElement?.closest("[data-category-id]")
      }
    })
  }

  // --- Drag and drop ---

  // Every <li data-category-id> carries this action, and dragstart bubbles, so
  // without stopPropagation each ancestor <li> would run this handler too and
  // overwrite dataTransfer with its own id — the outermost ancestor winning and
  // dragging the whole subtree instead of the grabbed node (CONEK-6222).
  dragStart(event) {
    const node = event.currentTarget.closest("[data-category-id]")
    if (!node) return

    event.stopPropagation()
    event.dataTransfer.setData("text/plain", node.dataset.categoryId)
    event.dataTransfer.effectAllowed = "move"
    node.classList.add("opacity-50")
  }

  // Same bubbling as dragStart: only the node that was actually dragged should
  // run this, so ancestors don't re-run the cleanup on themselves.
  dragEnd(event) {
    const node = event.currentTarget.closest("[data-category-id]")
    if (!node) return

    event.stopPropagation()
    node.classList.remove("opacity-50")
    this.clearIndicators()
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    this.clearIndicators()
    event.currentTarget.classList.add("drop-indicator")
  }

  dragLeave(event) {
    event.currentTarget.classList.remove("drop-indicator")
  }

  // A node consumes its own drop: the root container also listens for drop
  // (dropOnRoot), and drop bubbles, so without stopPropagation one gesture fired
  // a second, conflicting PATCH with new_parent_id: null and the surviving parent
  // came down to whichever request committed last (CONEK-6223).
  async drop(event) {
    event.preventDefault()
    event.stopPropagation()

    const dropZone = event.currentTarget
    const draggedId = event.dataTransfer.getData("text/plain")
    const dropTarget = dropZone.closest("[data-category-id]")
    dropZone.classList.remove("drop-indicator")

    if (!dropTarget || !draggedId) return

    const targetId = dropTarget.dataset.categoryId
    if (draggedId === targetId) return // don't drop on self

    // Moving a node into its own descendant would create a cycle. The server
    // rejects it with 422 either way, but a bare 422 leaves the user with no
    // explanation, so say why up front and don't send the write (CONEK-6224).
    if (this.isDescendantOf(dropTarget, draggedId)) {
      this.showToast(this.invalidMoveToastTarget)
      return
    }

    // Position = index among the target's existing children.
    const childrenContainer = dropTarget.querySelector(
      "[data-catalog--tree-target='children']",
    )
    const position = childrenContainer
      ? childrenContainer.querySelectorAll(":scope > [data-category-id]").length
      : 0

    await this.reorder(draggedId, targetId, position)
  }

  // The root container needs a dragover that calls preventDefault, otherwise the
  // browser refuses a drop in the empty space below the nodes. It must NOT clear
  // the drop indicators: dragover bubbles out of every row, so clearing here
  // wiped the highlight dragOver had just set and left the tree with no visible
  // drop target at all.
  dragOverRoot(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
  }

  // Only reached when no node consumed the drop (i.e. the empty area around the
  // nodes) — a drop on a node stops propagation in `drop` above.
  async dropOnRoot(event) {
    event.preventDefault()
    this.clearIndicators()

    const draggedId = event.dataTransfer.getData("text/plain")
    if (!draggedId) return

    const rootNodes = this.element.querySelectorAll(
      ":scope > [data-category-id]",
    )
    await this.reorder(draggedId, null, rootNodes.length)
  }

  // --- Helpers ---

  allDetails() {
    return this.element.querySelectorAll("details")
  }

  // True when `node` sits inside the subtree of the category `ancestorId`.
  isDescendantOf(node, ancestorId) {
    let current = node.parentElement?.closest("[data-category-id]")
    while (current) {
      if (current.dataset.categoryId === ancestorId) return true
      current = current.parentElement?.closest("[data-category-id]")
    }
    return false
  }

  // Toasts are rendered server-side into <template> tags by the view, so the
  // copy stays in Rails i18n and no design-system classes leak into JS. Cloning
  // into #toast-container lets Stimulus connect ui--toast (animation + dismiss).
  showToast(template) {
    const container = document.getElementById("toast-container")
    if (!container || !template) return

    const toast = template.content.firstElementChild.cloneNode(true)
    // The cloned template carries the id it was rendered with, so give each
    // toast its own. It has to be random rather than a per-controller counter:
    // a successful reorder replaces the tree — the controller's own element —
    // so Stimulus builds a fresh instance and any counter restarts, handing the
    // same id to a toast that is still on screen. Matches how ToastComponent
    // names a server-rendered toast (SecureRandom.hex).
    toast.id = `toast-reorder-${Math.random().toString(36).slice(2, 10)}`
    container.appendChild(toast)
  }

  clearIndicators() {
    this.element.querySelectorAll(".drop-indicator").forEach((el) => {
      el.classList.remove("drop-indicator")
    })
  }

  async reorder(id, newParentId, position) {
    if (!this.reorderUrlValue) return

    try {
      const response = await patch(this.reorderUrlValue, {
        body: JSON.stringify({
          id: id,
          new_parent_id: newParentId,
          position: position,
        }),
        contentType: "application/json",
        responseKind: "turbo-stream",
      })

      if (!response.ok) {
        // The server-side guardrails (cycle checks, permissions) answer with a
        // bare status and no body, so the message has to come from the view's
        // template — a console line is invisible to the user (CONEK-6224).
        // Nothing to roll back: the move is never applied optimistically, the
        // tree is only ever redrawn by the Turbo Stream a success returns.
        console.error("Reorder failed:", response.statusCode)
        this.showToast(this.errorToastTarget)
      }
    } catch (error) {
      console.error("Reorder request failed:", error)
      this.showToast(this.errorToastTarget)
    }
  }
}
